import React, { useMemo, useCallback } from "react";
import {
  Box,
  Text,
  Icon,
  EntitySelector,
  EntitySelectorContent,
  EntitySelectorInput,
  EntitySelectorSelected,
  EntitySelectorPool,
  EntitySelectorEmpty,
  toastManager,
  Tooltip,
  TooltipPopup,
  TooltipTrigger,
  type EntitySelectorConfig,
  type IconName,
} from "@salestrak/ui";
import { useAppData } from "#app/lib/data";
import { useWorkflowStages } from "#app/lib/hooks/use-workflow-stages";
import { useIssueViewMutations } from "#app/hooks/issue-view/use-issue-view-mutations";
import { canManageSharing } from "#app/lib/utils/access-level";
import { toastMutationError } from "#app/lib/utils/toast-errors";
import { useIssueViewCapabilities } from "./issue-view-capabilities";
import type { IssueViewStage } from "./issue-view-types";
import type { EntityRefValue, IssueRow } from "#app/lib/data";
import {
  WorkflowSelector,
  StageSelector,
  IssueSourceSelector,
  SelectionEntityTrigger,
  selectionToWorkflowEntity,
  selectionToUserEntity,
} from "#app/components/entity-selectors";
import { AddressPropertyInput } from "./address-property-input";
import { useIssueViewDialogState } from "./issue-view-dialog-state";
import { useAppNavigate } from "#app/lib/hooks/use-app-navigate";
import { removeIssueFromStageCache, insertIssueIntoStageCache } from "#app/hooks/kanban/kanban-cache-utils";
import { useUserPool, USER_POOL_ID } from "#app/components/entity-selectors/hooks/use-user-pool";
import {
  assigneeToSelection,
  useAssigneeSelection,
} from "#app/components/entity-selectors/use-assignee-selection";
import { useCreateIssueMode } from "#app/lib/create-issue-context";
import { useIssueSourceSettings } from "#app/lib/hooks/use-issue-source-settings";
import type { Omittable } from "@salestrak/ui";

function PropertyRow({
  icon,
  label,
  required,
  error,
  id,
  children,
}: Omittable<{
  icon: IconName;
  label: string;
  required?: boolean;
  error?: string;
  /** DOM id — the create-issue flow scrolls the first invalid row into view by it. */
  id?: string;
  children: React.ReactNode;
}>) {
  return (
    <Box id={id} direction="row" align="center" gap="sm" height={32}>
      <Box direction="row" align="center" gap="sm" width={120}>
        <Icon name={icon} size="md" color={error ? "destructive" : "muted"} />
        <Text size="sm" weight="normal" variant={error ? "destructive" : "muted"}>
          {label}
          {required && <Text variant="destructive"> *</Text>}
        </Text>
        {error ? (
          // The tooltip trigger renders its own element, so the icon gets a Box
          // host rather than becoming a button nobody can act on.
          <Tooltip>
            <TooltipTrigger render={<Box direction="row" align="center" />}>
              <Icon name="alert-circle" size="md" color="destructive" />
            </TooltipTrigger>
            <TooltipPopup side="right">
              <Text size="xs">{error}</Text>
            </TooltipPopup>
          </Tooltip>
        ) : null}
      </Box>
      {/* min-width 0: `flex-1` alone floors at content width, so a wide value
          (a one-line address) would stretch the row past the panel. */}
      <Box fill="flex" minWidth={0}>{children}</Box>
    </Box>
  );
}

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

type IssueViewPropertiesProps = Omittable<{
  issue?: IssueRow;
  stage?: IssueViewStage | null;
  workflowId?: string | null;
  address?: {
    id: string;
    addressId: string;
    wholeAddress?: string | null;
    latLng?: { lat: number; lng: number } | null;
  } | null;
  assignees?: EntityRefValue[];
  columns?: 1 | 2;
}>;

export function IssueViewProperties({
  issue,
  stage,
  workflowId: freshWorkflowId,
  address,
  assignees,
  columns = 1,
}: IssueViewPropertiesProps) {
  const { workflows } = useAppData();
  const createCtx = useCreateIssueMode();
  const isCreateMode = !!createCtx;
  const { settings: issueSourceSettings } = useIssueSourceSettings();
  const requireIssueSource = issueSourceSettings.requireIssueSource;

  const mutations = useIssueViewMutations();
  // Pure-view access (or comment-only) → property mutations no-op.
  // Create mode bypasses the gate because the issue doesn't exist
  // yet; the parent dialog already gated entry into create mode by
  // role.
  const { canEdit, level } = useIssueViewCapabilities();
  const editableHere = isCreateMode || canEdit;
  // Assignee adds run the same "they can't see this project" prompt as
  // @-mentions (handled inside useAssigneeSelection) — checked in the
  // background after the mutation fires, never blocking it.
  const viewerCanManageSharing = canManageSharing(level);
  // Wrap each mutation so a view/comment-tier viewer can't kick off
  // a write even if a callback fires. The backend rejects regardless,
  // but skipping the round-trip keeps the optimistic UI honest.
  const noop = useCallback(
    () => Promise.resolve({ data: undefined as never }) as never,
    [],
  );
  const { updateWorkflow, updateStage, updateIssue, updateAddress, updateAssignees } = editableHere
    ? mutations
    : {
        updateWorkflow: noop,
        updateStage: noop,
        updateIssue: noop,
        updateAddress: noop,
        updateAssignees: noop,
      };
  const { pool: userPool } = useUserPool();

  // In create mode, read from draft. In view mode, use props/issue.
  const currentWorkflowId = isCreateMode ? createCtx.draft.workflowId : (freshWorkflowId ?? issue?.workflowId ?? "");

  const { stages: workflowStages } = useWorkflowStages(isCreateMode ? undefined : currentWorkflowId);

  const stageOptions = useMemo(() => {
    if (isCreateMode) {
      return createCtx.stages.map((s) => ({
        id: s.id,
        stageId: s.stageId,
        name: s.name,
        type: s.type ?? null,
        icon: s.icon ?? null,
      }));
    }
    if (workflowStages.length > 0) return workflowStages;
    // While the full list loads, include the current stage so the trigger
    // can display the selected stage name immediately.
    if (stage) {
      return [
        {
          id: stage.id,
          stageId: stage.stageId,
          name: stage.name,
          type: stage.type ?? null,
          icon: stage.icon ?? null,
        },
      ];
    }
    return [];
  }, [isCreateMode, createCtx, workflowStages, stage]);

  const currentStageId = isCreateMode ? (createCtx.draft.stageId ?? "") : (stage?.stageId ?? issue?.stageId ?? "");

  const handleWorkflowSelect = useCallback(
    (workflowId: string) => {
      if (workflowId === currentWorkflowId) return;

      if (isCreateMode) {
        createCtx.updateDraft({ workflowId, stageId: null });
        return;
      }

      if (!issue) return;
      updateWorkflow({
        variables: { issueId: issue.issueId, workflowId },
        optimisticResponse: {
          updateIssue: {
            __typename: "Issue" as const,
            id: issue.id,
            issueId: issue.issueId,
            workflowId,
            stageId: issue.stageId,
            stage: {
              __typename: "Stage" as const,
              id: stage?.id ?? "",
              stageId: stage?.stageId ?? issue.stageId,
              name: stage?.name ?? "",
              sortOrder: 0,
              type: null,
              isDefault: false,
              workflowId,
              icon: {
                __typename: "Icon" as const,
                icon: stage?.icon?.icon ?? null,
                color: stage?.icon?.color ?? null,
                percentage: null,
              },
              age: {
                __typename: "StageAge" as const,
                disabled: null,
                allowManualUpdates: null,
              },
            },
          },
        },
        update: (cache) => {
          removeIssueFromStageCache(cache, currentStageId, issue.issueId);
        },
        onCompleted: (data) => {
          if (data.updateIssue.__typename === "Issue") {
            toastManager.add({ title: "Workflow updated", type: "success" });
          } else {
            toastManager.add({
              title:
                "message" in data.updateIssue
                  ? data.updateIssue.message
                  : "Failed to update workflow",
              type: "error",
            });
          }
        },
        onError: (err) => {
          toastMutationError(err, "Failed to update workflow");
        },
      });
    },
    [isCreateMode, createCtx, issue, currentWorkflowId, currentStageId, stage, updateWorkflow],
  );

  const handleStageSelect = useCallback(
    (stageId: string) => {
      if (stageId === currentStageId) return;

      if (isCreateMode) {
        createCtx.updateDraft({ stageId });
        return;
      }

      if (!issue) return;
      const selectedStage = stageOptions.find((s) => s.stageId === stageId);
      updateStage({
        variables: { issueId: issue.issueId, stageId },
        optimisticResponse: {
          updateIssue: {
            __typename: "Issue" as const,
            id: issue.id,
            issueId: issue.issueId,
            stageId,
            stage: {
              __typename: "Stage" as const,
              id: selectedStage?.id ?? "",
              stageId,
              name: selectedStage?.name ?? "",
              sortOrder: 0,
              type: null,
              isDefault: false,
              workflowId: currentWorkflowId,
              icon: {
                __typename: "Icon" as const,
                icon: selectedStage?.icon?.icon ?? null,
                color: selectedStage?.icon?.color ?? null,
                percentage: null,
              },
              age: {
                __typename: "StageAge" as const,
                disabled: null,
                allowManualUpdates: null,
              },
            },
          },
        },
        update: (cache) => {
          removeIssueFromStageCache(cache, currentStageId, issue.issueId);
          insertIssueIntoStageCache(cache, stageId, {
            issueId: issue.issueId,
            sortOrder: Number.MIN_SAFE_INTEGER,
          });
        },
        onCompleted: (data) => {
          if (data.updateIssue.__typename === "Issue") {
            toastManager.add({ title: "Stage updated", type: "success" });
          } else {
            toastManager.add({
              title:
                "message" in data.updateIssue
                  ? data.updateIssue.message
                  : "Failed to update stage",
              type: "error",
            });
          }
        },
        onError: (err) => {
          toastMutationError(err, "Failed to update stage");
        },
      });
    },
    [isCreateMode, createCtx, issue, currentStageId, currentWorkflowId, stageOptions, updateStage],
  );

  const currentIssueSourceId = isCreateMode
    ? (createCtx.draft.issueSourceId ?? "")
    : (issue?.issueSourceId ?? "");

  const handleIssueSourceSelect = useCallback(
    (issueSourceId: string) => {
      if (issueSourceId === currentIssueSourceId) return;

      if (isCreateMode) {
        createCtx.updateDraft({ issueSourceId });
        return;
      }

      if (!issue) return;
      // Matches issue-type changes: the source field is set via the generic
      // updateIssue mutation, which returns the full issue (with the resolved
      // issueSource) so Apollo updates the cache by id.
      updateIssue({
        variables: { issueId: issue.issueId, input: { issueSourceId } },
        onCompleted: (data) => {
          if (data.updateIssue.__typename === "Issue") {
            toastManager.add({ title: "Source updated", type: "success" });
          } else {
            toastManager.add({
              title:
                "message" in data.updateIssue
                  ? data.updateIssue.message
                  : "Failed to update source",
              type: "error",
            });
          }
        },
        onError: (err) => {
          toastMutationError(err, "Failed to update source");
        },
      });
    },
    [isCreateMode, createCtx, issue, currentIssueSourceId, updateIssue],
  );

  const handleAddressSelect = useCallback(
    (wholeAddress: string) => {
      if (isCreateMode) {
        createCtx.updateDraft({ address: { wholeAddress } });
        return;
      }
      if (!issue) return;
      updateAddress({
        variables: { issueId: issue.issueId, address: { wholeAddress } },
        optimisticResponse: {
          updateIssue: {
            __typename: "Issue" as const,
            id: issue.id,
            issueId: issue.issueId,
            addressId: issue.addressId ?? null,
            address: {
              __typename: "Address" as const,
              id: address?.id ?? `optimistic-${issue.issueId}`,
              addressId: address?.addressId ?? `optimistic-${issue.issueId}`,
              wholeAddress,
            },
          },
        },
        onCompleted: (data) => {
          if (data.updateIssue.__typename === "Issue") {
            toastManager.add({ title: "Address updated", type: "success" });
          } else {
            toastManager.add({
              title:
                "message" in data.updateIssue
                  ? data.updateIssue.message
                  : "Failed to update address",
              type: "error",
            });
          }
        },
        onError: (err) => {
          toastMutationError(err, "Failed to update address");
        },
      });
    },
    [isCreateMode, createCtx, issue, address, updateAddress],
  );

  const handleAddressClear = useCallback(() => {
    if (isCreateMode) {
      createCtx.updateDraft({ address: null });
      return;
    }
    if (!issue) return;
    updateAddress({
      variables: { issueId: issue.issueId, address: null },
      optimisticResponse: {
        updateIssue: {
          __typename: "Issue" as const,
          id: issue.id,
          issueId: issue.issueId,
          addressId: null,
          address: null,
        },
      },
      onCompleted: (data) => {
        if (data.updateIssue.__typename === "Issue") {
          toastManager.add({ title: "Address cleared", type: "success" });
        } else {
          toastManager.add({
            title:
              "message" in data.updateIssue
                ? data.updateIssue.message
                : "Failed to clear address",
            type: "error",
          });
        }
      },
      onError: (err) => {
        toastMutationError(err, "Failed to clear address");
      },
    });
  }, [isCreateMode, createCtx, issue, updateAddress]);

  // ---------------------------------------------------------------------------
  // Assignee entity-selector state (members-only, one changeable primary)
  // ---------------------------------------------------------------------------

  const assigneeInitialSelections = useMemo(() => {
    if (isCreateMode) return createCtx.draft.assignees;
    const poolMap = new Map(userPool.items.map((item) => [item.id, item]));
    // Defaulted inside the memo: an `?? []` fallback outside it is a new
    // array every render, and the memo would never hold.
    return (assignees ?? [])
      .filter((a) => a.user || poolMap.has(a.entityId))
      .map((a) => assigneeToSelection(a, poolMap));
  }, [isCreateMode, createCtx, assignees, userPool.items]);

  const assigneeConfig = useMemo<EntitySelectorConfig>(
    () => ({
      pools: [userPool],
      searchPlaceholder: "Search members...",
      emptyMessage: "No members found",
      noItemsMessage: "No members yet",
    }),
    [userPool],
  );

  const {
    draftSelections: assigneeDraftSelections,
    handleValueChange: handleAssigneeValueChange,
    handleOpenChange: handleAssigneeOpenChange,
    getSetPrimaryLabel,
    handleSetPrimary,
  } = useAssigneeSelection({
    initialSelections: assigneeInitialSelections,
    issue: isCreateMode || !issue ? null : { id: issue.id, issueId: issue.issueId },
    updateAssignees,
    viewerCanManageSharing,
    onDraftChange: isCreateMode
      ? (next) => createCtx.updateDraft({ assignees: next })
      : undefined,
  });

  const resolvedAddress = isCreateMode
    ? createCtx.draft.address
      ? { wholeAddress: createCtx.draft.address.wholeAddress }
      : undefined
    : address !== undefined
      ? address
      : issue?.address;

  const { desktopSize, setDesktopSize } = useIssueViewDialogState();
  const appNavigate = useAppNavigate();
  const focusLatLng = !isCreateMode ? (address?.latLng ?? null) : null;
  const focusAddressId = !isCreateMode ? address?.addressId : undefined;
  const handleShowOnMap = useCallback(() => {
    if (!focusLatLng || !issue?.issueId) return;
    if (desktopSize === "fullscreen") setDesktopSize("sidebar");
    const params = new URLSearchParams({
      lat: String(focusLatLng.lat),
      lng: String(focusLatLng.lng),
      z: "16",
      issueId: issue.issueId,
    });
    if (focusAddressId) params.set("addressId", focusAddressId);
    appNavigate.push(`/map?${params.toString()}`);
  }, [focusLatLng, focusAddressId, desktopSize, setDesktopSize, appNavigate, issue?.issueId]);

  const rows = (
    <>
      <PropertyRow icon="git-branch" label="Workflow">
        <WorkflowSelector
          value={currentWorkflowId}
          onValueChange={(ids) => {
            const next = ids[0];
            if (next) handleWorkflowSelect(next);
          }}
          // In create mode, only list workflows the viewer can create in.
          // (For an existing issue's workflow swap, entry was already gated
          // on viewer access — they're editing within the same scope.)
          editableOnly={isCreateMode}
        >
          <SelectionEntityTrigger
            toEntity={selectionToWorkflowEntity}
            placeholder="Select workflow..."
            title="Workflow"
            // View/comment tier: static value, picker can't open. (Gating must
            // live on the trigger — WorkflowSelector ignores its own `disabled`
            // when children are provided.)
            readOnly={!editableHere}
          />
          <EntitySelectorContent fit>
            <EntitySelectorInput />
            <EntitySelectorPool value="workflows" />
            <EntitySelectorEmpty>No workflows found</EntitySelectorEmpty>
          </EntitySelectorContent>
        </WorkflowSelector>
      </PropertyRow>

      <PropertyRow icon="circle-dot" label="Stage">
        <StageSelector
          workflowId={currentWorkflowId}
          value={currentStageId}
          onValueChange={handleStageSelect}
          readOnly={!editableHere}
        />
      </PropertyRow>

      <PropertyRow icon="users" label="Assignees">
        <EntitySelector
          config={assigneeConfig}
          value={assigneeDraftSelections}
          onValueChange={handleAssigneeValueChange}
          onOpenChange={handleAssigneeOpenChange}
        >
          <SelectionEntityTrigger
            toEntity={selectionToUserEntity}
            placeholder="Add assignees..."
            title="Assignees"
            emptyIcon="user-plus"
            readOnly={!editableHere}
          />
          <EntitySelectorContent size="sm">
            <EntitySelectorInput />
            <EntitySelectorSelected
              label="Assignees"
              hideWhenEmpty
              hideBadge
              hideSelectionHighlight
              maxHeight={160}
              getItemSecondaryActionLabel={getSetPrimaryLabel}
              onItemSecondaryAction={handleSetPrimary}
            />
            <EntitySelectorPool value={USER_POOL_ID} hideSelectionHighlight />
            <EntitySelectorEmpty>No members found</EntitySelectorEmpty>
          </EntitySelectorContent>
        </EntitySelector>
      </PropertyRow>

      <PropertyRow
        icon="tag"
        label="Source"
        required={isCreateMode && requireIssueSource}
        error={
          createCtx?.submitted && requireIssueSource && !currentIssueSourceId
            ? "Source is required"
            : undefined
        }
        id="issue-source"
      >
        <IssueSourceSelector
          value={currentIssueSourceId}
          onValueChange={handleIssueSourceSelect}
          readOnly={!editableHere}
        />
      </PropertyRow>

      <PropertyRow icon="map-pin" label="Address">
        <AddressPropertyInput
          wholeAddress={resolvedAddress?.wholeAddress}
          onSelect={handleAddressSelect}
          onClear={editableHere ? handleAddressClear : undefined}
          onShowOnMap={focusLatLng ? handleShowOnMap : undefined}
          readOnly={!editableHere}
        />
      </PropertyRow>

    </>
  );

  // Two even columns are a grid recipe on Box, which carries no `display` prop.
  return columns === 2 ? (
    <Box display="grid" columns={2} gap="sm">
      {rows}
    </Box>
  ) : (
    <Box gap="sm">{rows}</Box>
  );
}
